import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { ScoreBadge } from "@/components/app/ScoreBadge";
import { UploadPicker } from "@/components/app/UploadPicker";
import { Star } from "lucide-react";

export default function ProductOpportunity() {
  const { selectedBrandId } = useBrands();
  const [data, setData] = useState({ product_opportunity: [] });
  const [products, setProducts] = useState([]);
  const [uploadId, setUploadId] = useState("");

  useEffect(() => { setUploadId(""); }, [selectedBrandId]);

  useEffect(() => {
    if (!selectedBrandId) return;
    const params = new URLSearchParams({ brand_id: selectedBrandId });
    if (uploadId) params.append("upload_id", uploadId);
    Promise.all([
      api.get(`/dashboard/summary?${params}`),
      api.get(`/products?brand_id=${selectedBrandId}`),
    ]).then(([s, p]) => { setData(s.data); setProducts(p.data || []); });
  }, [selectedBrandId, uploadId]);

  const productById = Object.fromEntries(products.map((p) => [p.id, p]));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Intelligence</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Product Opportunity</h1>
          <p className="mt-1 text-sm text-slate-500">SKU-level opportunity from search demand, relevance & business fit.</p>
        </div>
        <UploadPicker
          brandId={selectedBrandId}
          value={uploadId}
          onChange={setUploadId}
          testId="product-opp-upload-picker"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="product-opportunity-grid">
        {(data.product_opportunity || []).map((p) => {
          const full = productById[p.product_id] || {};
          return (
            <div key={p.product_id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {full.is_hero_sku ? <Star strokeWidth={2} className="h-3.5 w-3.5 text-amber-500 fill-amber-400" /> : null}
                    <span className="text-[11px] uppercase tracking-widest text-slate-500">{full.product_type || "Product"}</span>
                  </div>
                  <h3 className="mt-0.5 text-base font-semibold tracking-tight text-slate-900">{p.product_name}</h3>
                  <div className="mt-1 text-[11px] font-mono text-slate-500">{full.sku || "—"} · {full.asin || "—"}</div>
                </div>
                <ScoreBadge score={p.avg_opportunity} label="OS" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Stat label="Keywords" value={p.matched_keywords} />
                <Stat label="Demand" value={p.avg_demand} score />
                <Stat label="Relevance" value={p.avg_relevance} score />
              </div>
              {full.benefits ? <p className="mt-3 text-xs text-slate-600 line-clamp-2">{full.benefits}</p> : null}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {full.inventory_status ? <Pill>{full.inventory_status}</Pill> : null}
                {full.lifecycle_stage ? <Pill>{full.lifecycle_stage}</Pill> : null}
                {full.is_hero_sku ? <Pill tone="amber">Hero SKU</Pill> : null}
              </div>
            </div>
          );
        })}
        {!data.product_opportunity?.length && <div className="text-sm text-slate-500">No data yet — upload a CSV first.</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, score }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      {score
        ? <ScoreBadge score={value} className="mt-0.5" />
        : <div className="text-sm font-semibold text-slate-900">{value}</div>}
    </div>
  );
}

function Pill({ tone = "slate", children }) {
  const map = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${map[tone]}`}>{children}</span>;
}
